<script setup lang="ts">
import { sanitizeGFALAmountInput } from "~/utils/input.utils";

const emit = defineEmits(["close"]);
const props = defineProps({
  collectible: {
    type: Object as PropType<Collectible>,
    required: true,
  },
  action: {
    type: String as PropType<"list" | "edit" | "cancel">,
    required: true,
  },
});

const actionTitle = {
  list: {
    title: "main.modalList.listTitle",
    successTitle: "main.modalList.success",
    successBody: "main.modalList.available",
  },
  edit: {
    title: "main.modalList.editTitle",
    successTitle: "main.modalList.editSuccess",
    successBody: "main.modalList.editSuccessDetail",
  },
  cancel: {
    title: "main.modalList.cancelTitle",
    successTitle: "main.modalList.cancelSuccess",
    successBody: "main.modalList.itemRemoved",
  },
};

const route = useRoute();
const router = useRouter();
const { trackEvent } = useGAnalytics();
const { listCollectible, unlistCollectible } = useCollectiblesController();
const isStartingListing = ref<boolean>(true);
const isModalLoaderShown = ref<boolean>(false);
const isModalEndShown = ref<boolean>(false);
const listingPrice = ref<string>("");
const editedListingPrice = ref<string>(
  props.collectible.price ? props.collectible.price.GFAL.toString() : ""
);
const success = ref<boolean>(false);
const errorMessage = ref<string>("");

const submitListing = async () => {
  listingPrice.value = sanitizeGFALAmountInput(listingPrice.value || "");
  isStartingListing.value = false;
  isModalLoaderShown.value = true;
  try {
    const res = await listCollectible(props.collectible.id, listingPrice.value);
    if (!res) {
      throw new Error("Failed to list collectible");
    }
    if (res.statusCode === 200) {
      trackEvent("list_success");
      success.value = true;
    } else {
      throw new Error("Failed to list collectible");
    }
  } catch (error: any) {
    if (error.statusCode === 406) {
      errorMessage.value = "errorMessages.listingInvalidMinimumPrice";
    }
    trackEvent("list_collectible_error");
    console.error(error);
  } finally {
    isModalLoaderShown.value = false;
    isModalEndShown.value = true;
  }
};

const editListing = async () => {
  isStartingListing.value = false;
  isModalLoaderShown.value = true;
  try {
    const res = await listCollectible(
      props.collectible.id,
      editedListingPrice.value
    );
    if (!res) {
      throw new Error("Failed to edit listing");
    }
    if (res.statusCode === 200) {
      trackEvent("list_edit_success");
      success.value = true;
    } else {
      throw new Error("Failed to edit listing");
    }
  } catch (error) {
    trackEvent("list_edit_error");
    console.error(error);
  } finally {
    isModalLoaderShown.value = false;
    isModalEndShown.value = true;
  }
};

const cancelListing = async () => {
  isStartingListing.value = false;
  isModalLoaderShown.value = true;
  try {
    const res = await unlistCollectible(props.collectible.id);
    if (!res) {
      throw new Error("Failed to cancel listing");
    }
    if (res.statusCode === 200) {
      trackEvent("list_cancel_success");
      success.value = true;
    } else {
      throw new Error("Failed to cancel listing");
    }
  } catch (error) {
    trackEvent("list_cancel_error");
    console.error(error);
  } finally {
    isModalLoaderShown.value = false;
    isModalEndShown.value = true;
  }
};

const currentRoute = (route.name as string).split("-")[0];
const onCloseEnd = () => {
  emit("close");
  if (currentRoute === "market") {
    router.push(`/market/${route.params.merchant}`);
  } else {
    router.push(`/collection/${props.collectible.merchantId}`);
  }
};
</script>
<template>
  <BaseLayoutModal
    width="medium"
    :hide-x="isModalLoaderShown"
    @close="
      isModalLoaderShown
        ? null
        : isModalEndShown
        ? onCloseEnd()
        : $emit('close')
    "
    @mouse-up="isModalLoaderShown ? null : $emit('close')">
    <template #header>
      {{ $t(actionTitle[action].title) }}
    </template>
    <template #content>
      <Transition mode="out-in">
        <ModalListingStart
          v-if="isStartingListing && action === 'list'"
          v-model:listing-price="listingPrice"
          :collectible="collectible"
          @listing-complete="submitListing"
          @close="$emit('close')" />
        <ModalListingEdit
          v-else-if="isStartingListing && action === 'edit'"
          v-model:edited-listing-price="editedListingPrice"
          :collectible="collectible"
          @edit-complete="editListing"
          @close="$emit('close')" />
        <ModalListingCancel
          v-else-if="isStartingListing && action === 'cancel'"
          :collectible="collectible"
          @cancel-complete="cancelListing"
          @close="$emit('close')" />
        <ModalLoader
          v-else-if="isModalLoaderShown"
          :title="$t(actionTitle[action].title)"
          :body-title="$t('main.modalLoader.transactionInProgress')"
          :body-subtitle="$t('main.modalLoader.transactionUnderway')" />
        <ModalListingEnd
          v-else-if="isModalEndShown"
          :success="success"
          :error-message="errorMessage"
          :header-text="$t(actionTitle[action].successTitle)"
          :body-text="$t(actionTitle[action].successBody)"
          @close="onCloseEnd" />
      </Transition>
    </template>
  </BaseLayoutModal>
</template>

<style scoped>
.v-enter-active {
  transition: all 0.3s ease-out;
}
.v-enter-from {
  opacity: 0;
  transform: translate(0, 1rem);
}
.v-enter-to {
  opacity: 1;
  transform: translate(0, 0);
}
.v-leave-active {
  transition: all 0.2s ease-in;
}
.v-leave-from {
  opacity: 1;
  transform: translate(0, 0);
}
.v-leave-to {
  opacity: 0;
  transform: translate(0, 1rem);
}
@media (min-width: 640px) {
  .v-enter-from,
  .v-leave-to {
    transform: translate(0, 0) scale(0.95);
  }
  .v-enter-to,
  .v-leave-from {
    transform: translate(0, 0) scale(1);
  }
}
</style>
